/**
 * Answering the owner from what is already on the screen.
 *
 * Every fact a question like "what is worker 2 doing?" needs is already in the
 * Control Center: the workers, what is running, what is waiting, which
 * computers are in touch. So the answer is COMPUTED, not generated - no model
 * call, no cost, no invented percentage, and it cannot be wrong about a state
 * it is reading directly.
 *
 * It answers the conversational half too - "are you there?", "hello", "thanks"
 * - because a control room that replies to those with a menu of its features
 * reads as a machine refusing to talk, which is exactly what the owner saw
 * three times in a row on the live screen.
 *
 * ORDER MATTERS HERE. "you are not responding to my question" contains the
 * words of an are-you-there question and is not one, so the complaint is read
 * before it. The small-talk lines match the protocol's own rule, length limit
 * included, so the planner and the screen agree about what was said.
 *
 * What it will not do is guess. A question it has no rows for gets the honest
 * answer: it does not know, and here is what it can see - never a menu of what
 * it is able to do, which the owner read as a canned brush-off.
 */
import { answerAboutBrunel, isQuestion, roadmapReadQuestion } from '@brunel/shared';
import type { NodePresence, RoadmapItemRow, RunningTask, Worker } from './data';
import { ownerGateWaitingLine, type OwnerGateWaitingItem } from './owner-gate-waiting';
import { workforceAnswer, workforceQuestion, type WorkforceSnapshot } from './workforce';

export interface DeskFacts {
  readonly workers: readonly Worker[];
  readonly running: readonly RunningTask[];
  /** Approvals waiting for the owner's decision. */
  readonly approvalsWaiting: number;
  /** Plans drafted and waiting for approve or reject. */
  readonly plansWaiting: number;
  /** Merged runs whose recorded owner gate is still outstanding. */
  readonly ownerGates?: readonly Pick<OwnerGateWaitingItem, 'pullRequest' | 'gate' | 'target' | 'version'>[];
  readonly nodes: readonly NodePresence[];
  readonly nodesConnected: number;
  /** #644 Slice D: the canonical workforce snapshot; undefined or null when it has not been read. */
  readonly workforce?: WorkforceSnapshot | null;
  /** Null means the canonical roadmap read failed; [] means it is empty. */
  readonly roadmap?: readonly RoadmapItemRow[] | null;
}

const CAN_ANSWER = 'I can tell you what a worker is doing, who is free, what is running, '
  + 'what is waiting on you and which computers are in touch.';
const FOR_WORK = 'Tell me what you want done and it goes to a Worker through the normal plan and dispatch.';
/** The protocol's own limit: longer than this and the greeting has an order after it. */
const SMALL_TALK_MAX = 24;

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function quoted(title: string): string {
  return `“${title}”`;
}

function aboutOneWorker(number: number, facts: DeskFacts): string {
  const worker = facts.workers.find((w) => w.number === number);
  if (worker === undefined) return `There is no Worker ${number}. ${workforceLine(facts)}`;
  const run = facts.running.find((r) => r.worker_number === number);
  if (run !== undefined) {
    return `Worker ${number} is working on ${quoted(run.title)} in ${run.project_name}, on ${run.node_name}.`;
  }
  return `Worker ${number} is ${worker.status.replace('_', ' ')} and has nothing running.`;
}

function workforceLine(facts: DeskFacts): string {
  if (facts.workers.length === 0) return 'There are no workers yet.';
  const numbers = facts.workers.map((w) => w.number).sort((a, b) => a - b).join(', ');
  return `The workers are ${numbers}.`;
}

function idleLine(facts: DeskFacts): string {
  // #644 Slice D: available means usable now (session, machine, exact runtime, no outage),
  // never merely "has nothing running".
  if (facts.workforce) {
    const available = facts.workforce.workers.filter((w) => w.state === 'available').map((w) => w.number).sort((a, b) => a - b);
    if (facts.workforce.counts.total === 0) return 'There are no workers yet.';
    if (available.length === 0) return 'No Worker is available right now.';
    return `${available.length === 1 ? 'Worker' : 'Workers'} ${available.join(', ')} ${available.length === 1 ? 'is' : 'are'} available.`;
  }
  const busy = new Set(facts.running.map((r) => r.worker_number));
  const idle = facts.workers.filter((w) => !busy.has(w.number)).map((w) => w.number).sort((a, b) => a - b);
  if (facts.workers.length === 0) return 'There are no workers yet.';
  if (idle.length === 0) return 'Every worker has something running.';
  return `${idle.length === 1 ? 'Worker' : 'Workers'} ${idle.join(', ')} ${idle.length === 1 ? 'is' : 'are'} free.`;
}

function runningLine(facts: DeskFacts): string {
  if (facts.running.length === 0) return 'Nothing is running.';
  const titles = facts.running
    .slice(0, 4)
    .map((r) => `${r.worker_number === null ? r.node_name : `Worker ${r.worker_number}`} on ${quoted(r.title)}`)
    .join('; ');
  const more = facts.running.length > 4 ? `, and ${facts.running.length - 4} more` : '';
  return `${count(facts.running.length, 'task is', 'tasks are')} running: ${titles}${more}.`;
}

function waitingLine(facts: DeskFacts): string {
  const parts: string[] = [];
  if (facts.plansWaiting > 0) parts.push(count(facts.plansWaiting, 'plan', 'plans') + ' to approve');
  if (facts.approvalsWaiting > 0) parts.push(count(facts.approvalsWaiting, 'approval', 'approvals'));
  const gates = (facts.ownerGates ?? []).map((gate) => ownerGateWaitingLine(gate));
  if (parts.length === 0 && gates.length === 0) return 'Nothing is waiting on you.';
  if (parts.length === 0) return `Waiting on you: ${gates.join('; ')}.`;
  if (gates.length === 0) return `Waiting on you: ${parts.join(' and ')}.`;
  return `Waiting on you: ${parts.join(' and ')}. ${gates.join('; ')}.`;
}

function computersLine(facts: DeskFacts): string {
  if (facts.nodes.length === 0) return 'No computer is connected yet. You add one in Settings & Options.';
  const out = facts.nodes.length - facts.nodesConnected;
  if (out === 0) return `${count(facts.nodesConnected, 'computer is', 'computers are')} in touch.`;
  return `${facts.nodesConnected} of ${facts.nodes.length} computers are in touch; ${count(out, 'is', 'are')} not reporting in.`;
}

/** The one-line state of the room, for the answers that should carry it. */
function stateLine(facts: DeskFacts): string {
  return `${computersLine(facts)} ${runningLine(facts)} ${waitingLine(facts)}`;
}

/**
 * The answer to a question the owner asked, or null when the command is an
 * order rather than a question - orders are planned, not answered.
 */
export function answerFor(command: string, facts: DeskFacts): string | null {
  const asked = command.toLowerCase().trim();

  const roadmapScope = roadmapReadQuestion(command);
  if (roadmapScope !== null) {
    if (facts.roadmap == null) return 'I could not read the roadmap right now. Please try again.';
    const horizonOrder = { current: 0, next: 1, later: 2 };
    const items = facts.roadmap
      .filter((item) => roadmapScope === 'all' || item.horizon === 'current')
      .sort((a, b) => horizonOrder[a.horizon] - horizonOrder[b.horizon]
        || a.rank - b.rank || a.created_at.localeCompare(b.created_at));
    if (items.length === 0) return roadmapScope === 'current'
      ? 'There are no jobs in the current roadmap horizon.'
      : 'There are no roadmap jobs.';
    const label = roadmapScope === 'current' ? 'Current roadmap jobs' : 'Roadmap jobs';
    return `${label} (${items.length}):\n${items.map((item, index) =>
      `${index + 1}. ${item.title} — ${item.status.replace('_', ' ')}${roadmapScope === 'all' ? `, ${item.horizon}` : ''}`
    ).join('\n')}`;
  }

  // #644 Slice D: the closed workforce questions, answered from the one canonical snapshot.
  const aboutWorkforce = workforceQuestion(command);
  if (aboutWorkforce !== null) return workforceAnswer(aboutWorkforce, facts.workforce);

  // Said to Brunel rather than asked of it.
  if (asked.length <= SMALL_TALK_MAX && /^(hi|hey|hello|yo|good (morning|afternoon|evening)|morning|evening)\b/.test(asked)) {
    return `Hello. ${stateLine(facts)}`;
  }
  if (asked.length <= SMALL_TALK_MAX && /^(thanks|thank you|cheers)\b/.test(asked)) {
    return 'Noted.';
  }

  // Asked about Brunel itself, which is not a question about the work and has to be read
  // before the question gate: "you are inside brunel our application" is a statement, and
  // planning it into six tasks is how the owner found out nobody had told it what it is.
  const aboutItself = answerAboutBrunel(command);
  if (aboutItself !== null) return aboutItself;

  if (!isQuestion(command)) return null;

  // The owner saying the answers are not answers. Say what is true rather than
  // repeating the menu that annoyed them. Read BEFORE are-you-there, which it
  // otherwise trips on its way past.
  if (/\b(not making sense|makes no sense|not responding|no sense|useless|nonsense|not answering)\b/.test(asked)) {
    return 'Fair. I answer from the rows on this screen, so I am exact about the work and thin about everything else. '
      + `What I can see now: ${stateLine(facts)}`;
  }

  // "are you there", "are you working", "is this thing on" - asking whether
  // Brunel is alive, not what it is capable of and not about the work.
  if (/\b(are you|you there|is this thing|can you hear|do you work|working yet|functional|alive|online|awake|up and running|respond to me)\b/.test(asked)
    && !/\b(worker|workers|computer|computers|node|nodes|task|tasks|plan|plans)\b/.test(asked)) {
    return `Yes - I am here and reading everything you send. ${stateLine(facts)}`;
  }

  if (/\b(what can you|what do you do|help|capabilities|commands)\b/.test(asked)) {
    return `${CAN_ANSWER} ${FOR_WORK}`;
  }

  const worker = /\bworker\s*(\d{1,3})\b/.exec(asked) ?? /\b(\d{1,3})\s*(?:is|doing)\b/.exec(asked);
  if (worker !== null) return aboutOneWorker(Number(worker[1]), facts);

  if (/\b(idle|free|available|spare)\b/.test(asked)) return `${idleLine(facts)} ${runningLine(facts)}`;
  if (/\b(waiting|approve|approval|attention|decide|decision)\b/.test(asked)) return waitingLine(facts);
  if (/\b(computer|computers|node|nodes|machine|machines|connected|touch|online|offline)\b/.test(asked)) return computersLine(facts);
  if (/\b(running|happening|going on|busy|work|working|doing|status|update)\b/.test(asked)) {
    return `${runningLine(facts)} ${waitingLine(facts)}`;
  }
  if (/\b(worker|workers|team|workforce|how many)\b/.test(asked)) return `${workforceLine(facts)} ${idleLine(facts)}`;

  // Nothing on this screen answers it: say so, and say what is known, rather than recite a menu.
  return `I do not have a recorded fact on this screen that answers that, so I will not guess. What I can see now: ${stateLine(facts)}`;
}
